import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import type { AreaForecast, WbgtTimeline } from '@heat-pwa/types';
import { useWbgtTimelineQuery } from '../wbgt/useWbgtQuery.js';
import { useForecastsQuery } from '../forecast/useForecastsQuery.js';
import { WBGT_SCALE } from '../wbgt/wbgtScale.js';
import { TEMP_BANDS } from '../../lib/tempColor.js';
import { ACTIVE_TILE_SOURCE } from '../../lib/tileSource.js';
import { Legend } from './Legend.js';
import { ControlPanel } from './ControlPanel.js';
import { SettingsSheet } from './SettingsSheet.js';
import { renderPopup, type PopupPayload } from './popup.js';
import {
  fetchClass10,
  computeCentroids,
  computeNearestStations,
  type Class10FeatureCollection,
} from './class10.js';
import type { MapMode } from './types.js';

const WBGT_SRC = 'wbgt-pts';
const WBGT_CIRCLE = 'wbgt-circle';
const WBGT_VALUE_LABEL = 'wbgt-value-label';
const FORECAST_SRC = 'forecast-pts';
const FORECAST_LAYER = 'forecast-circle';
const FORECAST_TEMP_LABEL = 'forecast-temp-label';
const FORECAST_NAME_LABEL = 'forecast-name-label';
const CLASS10_SRC = 'class10';
const CLASS10_FILL = 'class10-fill';
const CLASS10_STROKE = 'class10-stroke';

type FCol = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    geometry: { type: 'Point'; coordinates: [number, number] };
    properties: Record<string, string | number | null>;
  }>;
};

const EMPTY_FC: FCol = { type: 'FeatureCollection', features: [] };

export function MapView({
  mode,
  onModeChange,
}: {
  mode: MapMode;
  onModeChange: (m: MapMode) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  const wbgtQ = useWbgtTimelineQuery();
  const forecastQ = useForecastsQuery();
  const [wbgtIdx, setWbgtIdx] = useState(0);
  const [forecastIdx, setForecastIdx] = useState(0);
  const [selected, setSelected] = useState<PopupPayload | null>(null);
  const popupRef = useRef<maplibregl.Popup | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [class10, setClass10] = useState<Class10FeatureCollection | null>(null);

  // Lazy-load the class10 boundary GeoJSON once per session.
  useEffect(() => {
    let cancelled = false;
    fetchClass10()
      .then((fc) => {
        if (!cancelled) setClass10(fc);
      })
      .catch((err) => console.warn('class10 load failed', err));
    return () => {
      cancelled = true;
    };
  }, []);

  // polygon id → nearest WBGT station code
  const polygonToStation = useMemo(() => {
    if (!class10 || !wbgtQ.data) return null;
    const centroids = computeCentroids(class10);
    return computeNearestStations(centroids, wbgtQ.data.stations);
  }, [class10, wbgtQ.data]);

  const wbgtTimestamps = wbgtQ.data?.timestamps ?? [];
  const forecastDates = useMemo(
    () => deriveForecastDates(forecastQ.data?.forecasts ?? []),
    [forecastQ.data],
  );

  const wbgtIdxClamped = clamp(wbgtIdx, wbgtTimestamps.length);
  const forecastIdxClamped = clamp(forecastIdx, forecastDates.length);

  // Auto-pick the "now" timestamp on first WBGT load.
  useEffect(() => {
    if (!wbgtTimestamps.length) return;
    const now = Date.now();
    let best = 0;
    let bestDelta = Number.POSITIVE_INFINITY;
    wbgtTimestamps.forEach((iso, i) => {
      const t = Date.parse(iso);
      if (Number.isNaN(t)) return;
      const d = Math.abs(t - now);
      if (d < bestDelta) {
        best = i;
        bestDelta = d;
      }
    });
    setWbgtIdx(best);
    // intentionally run only on first non-empty timestamps load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wbgtTimestamps.length > 0]);

  const wbgtFc = useMemo(
    () => buildWbgtFc(wbgtQ.data, wbgtIdxClamped),
    [wbgtQ.data, wbgtIdxClamped],
  );
  const forecastFc = useMemo(
    () => buildForecastFc(forecastQ.data?.forecasts ?? [], forecastIdxClamped),
    [forecastQ.data, forecastIdxClamped],
  );

  // Map init.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initial = loadSavedView();
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildRasterStyle(),
      center: initial?.center ?? [137.5, 36.2],
      zoom: initial?.zoom ?? 4.5,
      attributionControl: false,
    });
    // Attribution rendered inside ControlPanel instead of maplibre's
    // floating widget so it doesn't overlap the slider on mobile.
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    let saveTimer: number | null = null;
    const persist = () => {
      if (saveTimer != null) window.clearTimeout(saveTimer);
      saveTimer = window.setTimeout(() => {
        const c = map.getCenter();
        saveView({ center: [c.lng, c.lat], zoom: map.getZoom() });
      }, 400);
    };
    map.on('moveend', persist);
    map.on('zoomend', persist);
    mapRef.current = map;
    if (typeof window !== 'undefined') {
      (window as unknown as { __map?: maplibregl.Map }).__map = map;
    }
    return () => {
      map.off('moveend', persist);
      map.off('zoomend', persist);
      if (saveTimer != null) window.clearTimeout(saveTimer);
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Source/layer setup + data updates.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      ensureSources(map, class10);
      ensureLayers(map);
      (map.getSource(WBGT_SRC) as maplibregl.GeoJSONSource).setData(wbgtFc);
      (map.getSource(FORECAST_SRC) as maplibregl.GeoJSONSource).setData(forecastFc);
      if (class10) {
        const src = map.getSource(CLASS10_SRC) as maplibregl.GeoJSONSource | undefined;
        if (src) src.setData(class10 as unknown as GeoJSON.FeatureCollection);
      }
      setLayerVisibility(map, mode);
    };
    if (map.isStyleLoaded()) apply();
    else map.once('load', apply);
  }, [wbgtFc, forecastFc, mode, class10]);

  // Recolour the class10 polygons whenever the WBGT slider, data, or
  // polygon→station mapping changes. Uses setFeatureState so the fill
  // paint expression reads the level via `feature-state` without us
  // re-setting the source data each time.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !class10 || !polygonToStation || !wbgtQ.data) return;
    const ts = wbgtTimestamps[wbgtIdxClamped];
    if (!ts) return;
    const stationByCode = new Map(wbgtQ.data.stations.map((s) => [s.stationCode, s]));
    const apply = () => {
      for (const feat of class10.features) {
        const stationCode = polygonToStation.get(feat.id);
        const station = stationCode ? stationByCode.get(stationCode) : undefined;
        const sample = station?.samples.find((x) => x.observedAt === ts);
        map.setFeatureState(
          { source: CLASS10_SRC, id: feat.id },
          { level: sample?.level ?? 0 },
        );
      }
    };
    if (map.isStyleLoaded() && map.getSource(CLASS10_SRC)) apply();
    else map.once('idle', apply);
  }, [class10, polygonToStation, wbgtQ.data, wbgtTimestamps, wbgtIdxClamped]);

  // Auto-clear selection when switching modes (entities differ).
  useEffect(() => {
    setSelected(null);
  }, [mode]);

  // Popup lifecycle: stay alive as long as `selected` is set; refresh
  // its content whenever the time/date slider or underlying data changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!selected) {
      if (popupRef.current) {
        popupRef.current.remove();
        popupRef.current = null;
      }
      return;
    }
    const html = renderPopup(selected, {
      wbgt: { timeline: wbgtQ.data, timestamp: wbgtTimestamps[wbgtIdxClamped] },
      forecast: { forecasts: forecastQ.data?.forecasts ?? [], date: forecastDates[forecastIdxClamped] },
    });
    if (!html) return;
    if (popupRef.current) {
      popupRef.current.setLngLat([selected.lng, selected.lat]).setHTML(html);
    } else {
      const popup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        offset: 14,
        maxWidth: '280px',
        className: 'heat-popup',
      })
        .setLngLat([selected.lng, selected.lat])
        .setHTML(html)
        .addTo(map);
      popup.on('close', () => setSelected(null));
      popupRef.current = popup;
    }
  }, [
    selected,
    wbgtQ.data,
    wbgtTimestamps,
    wbgtIdxClamped,
    forecastQ.data,
    forecastDates,
    forecastIdxClamped,
  ]);

  // Click handlers.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onWbgtClick = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const feat = e.features?.[0];
      if (!feat || feat.geometry.type !== 'Point') return;
      const p = feat.properties as Record<string, unknown> | null;
      if (!p) return;
      const [lng, lat] = feat.geometry.coordinates as [number, number];
      setSelected({
        kind: 'wbgt',
        stationCode: String(p.stationCode ?? ''),
        lat,
        lng,
      });
    };
    const onForecastClick = (
      e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] },
    ) => {
      const feat = e.features?.[0];
      if (!feat || feat.geometry.type !== 'Point') return;
      const p = feat.properties as Record<string, unknown> | null;
      if (!p) return;
      const [lng, lat] = feat.geometry.coordinates as [number, number];
      setSelected({
        kind: 'forecast',
        spotCode: String(p.spotCode ?? ''),
        lat,
        lng,
      });
    };
    map.on('click', WBGT_CIRCLE, onWbgtClick);
    map.on('click', FORECAST_LAYER, onForecastClick);
    for (const layer of [WBGT_CIRCLE, FORECAST_LAYER]) {
      map.on('mouseenter', layer, () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', layer, () => (map.getCanvas().style.cursor = ''));
    }
    return () => {
      map.off('click', WBGT_CIRCLE, onWbgtClick);
      map.off('click', FORECAST_LAYER, onForecastClick);
    };
  }, []);

  const isLoading = mode === 'wbgt' ? wbgtQ.isLoading : forecastQ.isLoading;
  const error = mode === 'wbgt' ? wbgtQ.error : forecastQ.error;
  const hasData = mode === 'wbgt' ? !!wbgtQ.data : !!forecastQ.data;

  const timeLabel =
    mode === 'wbgt'
      ? formatHourLabel(wbgtTimestamps[wbgtIdxClamped])
      : formatDateLabel(forecastDates[forecastIdxClamped]);

  const ticks = useMemo(
    () =>
      mode === 'wbgt'
        ? wbgtTicks(wbgtTimestamps)
        : forecastDates.map<{ position: number; major: boolean }>((_, i) => ({
            position: forecastDates.length <= 1 ? 0 : i / (forecastDates.length - 1),
            major: true,
          })),
    [mode, wbgtTimestamps, forecastDates],
  );

  return (
    <div className="relative h-full w-full">
      <div ref={containerRef} className="absolute inset-0" />
      <Legend mode={mode} />
      {isLoading && !hasData && (
        <div className="absolute inset-x-0 top-0 bg-white/80 px-3 py-2 text-center text-xs text-slate-600">
          データを取得しています…
        </div>
      )}
      {error && !hasData && (
        <div className="absolute inset-x-0 top-0 bg-red-50 px-3 py-2 text-center text-xs text-red-700">
          {mode === 'wbgt'
            ? 'WBGT データ取得に失敗しました'
            : '予報データ取得に失敗しました（KV 未投入の可能性）'}
        </div>
      )}
      <ControlPanel
        mode={mode}
        onModeChange={onModeChange}
        timeLabel={timeLabel}
        ticks={ticks}
        fetchedLabel={fetchedLabel(mode, wbgtQ.data?.fetchedAt, forecastQ.data?.fetchedAt)}
        index={mode === 'wbgt' ? wbgtIdxClamped : forecastIdxClamped}
        max={(mode === 'wbgt' ? wbgtTimestamps.length : forecastDates.length) - 1}
        onIndexChange={(i) => (mode === 'wbgt' ? setWbgtIdx(i) : setForecastIdx(i))}
        onSettings={() => setSettingsOpen(true)}
      />
      {settingsOpen && <SettingsSheet onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

function ensureSources(map: maplibregl.Map, class10: Class10FeatureCollection | null) {
  if (!map.getSource(WBGT_SRC)) {
    map.addSource(WBGT_SRC, { type: 'geojson', data: EMPTY_FC });
  }
  if (!map.getSource(FORECAST_SRC)) {
    map.addSource(FORECAST_SRC, { type: 'geojson', data: EMPTY_FC });
  }
  if (class10 && !map.getSource(CLASS10_SRC)) {
    map.addSource(CLASS10_SRC, {
      type: 'geojson',
      data: class10 as unknown as GeoJSON.FeatureCollection,
    });
  }
}

function ensureLayers(map: maplibregl.Map) {
  // class10 layers must paint below the WBGT dots; when they're added
  // after the WBGT layers already exist we still need to slip them
  // underneath with the beforeId parameter.
  const wbgtBeforeId = map.getLayer(WBGT_CIRCLE) ? WBGT_CIRCLE : undefined;
  if (map.getSource(CLASS10_SRC) && !map.getLayer(CLASS10_FILL)) {
    map.addLayer(
      {
        id: CLASS10_FILL,
        type: 'fill',
        source: CLASS10_SRC,
        paint: {
          'fill-color': fillLevelColorExpression(),
          'fill-opacity': 0.45,
        },
      },
      wbgtBeforeId,
    );
  }
  if (map.getSource(CLASS10_SRC) && !map.getLayer(CLASS10_STROKE)) {
    map.addLayer(
      {
        id: CLASS10_STROKE,
        type: 'line',
        source: CLASS10_SRC,
        paint: {
          'line-color': '#ffffff',
          'line-width': 0.8,
          'line-opacity': 0.7,
        },
      },
      wbgtBeforeId,
    );
  }
  if (!map.getLayer(WBGT_CIRCLE)) {
    map.addLayer({
      id: WBGT_CIRCLE,
      type: 'circle',
      source: WBGT_SRC,
      paint: {
        'circle-color': levelColorExpression('level'),
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 6,
          6, 9,
          8, 13,
          10, 17,
          13, 21,
        ],
        'circle-stroke-color': '#1f2937',
        'circle-stroke-width': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 0.9,
          8, 1.2,
          12, 1.6,
        ],
      },
    });
  }
  if (!map.getLayer(WBGT_VALUE_LABEL)) {
    map.addLayer({
      id: WBGT_VALUE_LABEL,
      type: 'symbol',
      source: WBGT_SRC,
      layout: {
        'text-field': ['to-string', ['round', ['get', 'value']]],
        'text-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8, 9,
          10, 11,
          13, 14,
        ],
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: {
        'text-color': '#ffffff',
        'text-opacity': [
          'interpolate',
          ['linear'],
          ['zoom'],
          7, 0,
          8.5, 1,
        ],
      },
    });
  }
  if (!map.getLayer(FORECAST_LAYER)) {
    map.addLayer({
      id: FORECAST_LAYER,
      type: 'circle',
      source: FORECAST_SRC,
      paint: {
        'circle-color': tempColorExpression(),
        'circle-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          4, 14,
          8, 18,
          12, 22,
        ],
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    });
  }
  if (!map.getLayer(FORECAST_TEMP_LABEL)) {
    map.addLayer({
      id: FORECAST_TEMP_LABEL,
      type: 'symbol',
      source: FORECAST_SRC,
      layout: {
        'text-field': [
          'case',
          ['<=', ['get', 'tmax'], TEMP_MISSING],
          '',
          ['concat', ['to-string', ['round', ['get', 'tmax']]], '°'],
        ],
        'text-size': 15,
        'text-font': ['Noto Sans Regular'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
      },
      paint: { 'text-color': '#ffffff' },
    });
  }
  if (!map.getLayer(FORECAST_NAME_LABEL)) {
    map.addLayer({
      id: FORECAST_NAME_LABEL,
      type: 'symbol',
      source: FORECAST_SRC,
      layout: {
        'text-field': ['get', 'spotName'],
        'text-size': 11,
        'text-font': ['Noto Sans Regular'],
        'text-offset': [0, 1.6],
        'text-anchor': 'top',
        'text-optional': true,
      },
      paint: {
        'text-color': '#1f2937',
        'text-halo-color': '#ffffff',
        'text-halo-width': 1.4,
      },
    });
  }
}

function setLayerVisibility(map: maplibregl.Map, mode: MapMode) {
  const wbgtVisible = mode === 'wbgt';
  for (const id of [CLASS10_FILL, CLASS10_STROKE, WBGT_CIRCLE, WBGT_VALUE_LABEL]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', wbgtVisible ? 'visible' : 'none');
  }
  for (const id of [FORECAST_LAYER, FORECAST_TEMP_LABEL, FORECAST_NAME_LABEL]) {
    if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', wbgtVisible ? 'none' : 'visible');
  }
}

function buildWbgtFc(timeline: WbgtTimeline | undefined, idx: number): FCol {
  if (!timeline) return EMPTY_FC;
  const ts = timeline.timestamps[idx];
  if (!ts) return EMPTY_FC;
  const features: FCol['features'] = [];
  for (const s of timeline.stations) {
    const sample = s.samples.find((x) => x.observedAt === ts);
    if (!sample) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
      properties: {
        stationCode: s.stationCode,
        stationName: s.stationName,
        prefecture: s.prefecture,
        value: sample.value,
        level: sample.level,
        observedAt: sample.observedAt,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

const TEMP_MISSING = -999;

function buildForecastFc(forecasts: AreaForecast[], idx: number): FCol {
  const features: FCol['features'] = [];
  for (const f of forecasts) {
    const day = f.daily[idx];
    if (!day) continue;
    if (f.lat == null || f.lng == null) continue;
    features.push({
      type: 'Feature',
      geometry: { type: 'Point', coordinates: [f.lng, f.lat] },
      properties: {
        spotCode: f.areaCode,
        spotName: f.areaName,
        prefecture: f.prefecture ?? '',
        date: day.date,
        tmax: day.tmax ?? TEMP_MISSING,
        tmin: day.tmin ?? TEMP_MISSING,
      },
    });
  }
  return { type: 'FeatureCollection', features };
}

function levelColorExpression(prop: string): maplibregl.ExpressionSpecification {
  const stops: Array<number | string> = [];
  for (const band of WBGT_SCALE) {
    stops.push(band.level, band.color);
  }
  return [
    'match',
    ['get', prop],
    ...stops,
    WBGT_SCALE[WBGT_SCALE.length - 1]!.color,
  ] as unknown as maplibregl.ExpressionSpecification;
}

function fillLevelColorExpression(): maplibregl.ExpressionSpecification {
  const stops: Array<number | string> = [];
  for (const band of WBGT_SCALE) {
    stops.push(band.level, band.color);
  }
  // Polygon level lives in feature-state. Unmapped polygons (level = 0
  // or missing) render as transparent so the base map shows through.
  return [
    'match',
    ['coalesce', ['feature-state', 'level'], 0],
    0,
    'rgba(0,0,0,0)',
    ...stops,
    'rgba(0,0,0,0)',
  ] as unknown as maplibregl.ExpressionSpecification;
}

function tempColorExpression(): maplibregl.ExpressionSpecification {
  // step on tmax: ascending min thresholds → color of band whose min ≤ value.
  const ordered = [...TEMP_BANDS].sort((a, b) => a.min - b.min);
  const lowest = ordered[0]!;
  const steps: Array<number | string> = [lowest.color];
  for (let i = 1; i < ordered.length; i++) {
    const b = ordered[i]!;
    steps.push(b.min, b.color);
  }
  return [
    'case',
    ['<=', ['get', 'tmax'], TEMP_MISSING],
    '#94a3b8',
    ['step', ['get', 'tmax'], ...steps],
  ] as unknown as maplibregl.ExpressionSpecification;
}

function buildRasterStyle(): maplibregl.StyleSpecification {
  return {
    version: 8,
    sources: {
      raster: {
        type: 'raster',
        tiles: ACTIVE_TILE_SOURCE.tileUrls,
        tileSize: 256,
        attribution: ACTIVE_TILE_SOURCE.attribution,
        maxzoom: ACTIVE_TILE_SOURCE.maxZoom,
        minzoom: ACTIVE_TILE_SOURCE.minZoom,
      },
    },
    layers: [{ id: 'raster', type: 'raster', source: 'raster' }],
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
  };
}

function deriveForecastDates(forecasts: AreaForecast[]): string[] {
  // Only surface dates that have at least one spot with a non-null tmax.
  // JMA omits the same-day max once the morning's max is past, which would
  // otherwise pin the slider's first position on an all-grey day.
  const dateValidity = new Map<string, boolean>();
  for (const f of forecasts) {
    for (const d of f.daily) {
      dateValidity.set(d.date, (dateValidity.get(d.date) ?? false) || d.tmax != null);
    }
  }
  return [...dateValidity.entries()]
    .filter(([, valid]) => valid)
    .map(([date]) => date)
    .sort();
}

function clamp(i: number, len: number): number {
  if (len <= 0) return 0;
  if (i < 0) return 0;
  if (i >= len) return len - 1;
  return i;
}

function formatHourLabel(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
}

type SavedView = { center: [number, number]; zoom: number };
const VIEW_STORAGE_KEY = 'heat-pwa:map-view';

function loadSavedView(): SavedView | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (
      !Array.isArray(v.center) ||
      v.center.length !== 2 ||
      typeof v.center[0] !== 'number' ||
      typeof v.center[1] !== 'number' ||
      typeof v.zoom !== 'number'
    ) {
      return null;
    }
    return { center: [v.center[0], v.center[1]], zoom: v.zoom };
  } catch {
    return null;
  }
}

function saveView(v: SavedView): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIEW_STORAGE_KEY, JSON.stringify(v));
  } catch {
    // quota / disabled storage — silently ignore
  }
}

function fetchedLabel(mode: MapMode, wbgtAt: string | undefined, forecastAt: string | undefined): string {
  const iso = mode === 'wbgt' ? wbgtAt : forecastAt;
  if (!iso) return '最終更新: 取得中…';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '最終更新: ' + iso;
    return (
      '最終更新: ' +
      new Intl.DateTimeFormat('ja-JP', {
        timeZone: 'Asia/Tokyo',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d)
    );
  } catch {
    return '最終更新: ' + iso;
  }
}

function wbgtTicks(timestamps: string[]): Array<{ position: number; major: boolean }> {
  if (timestamps.length <= 1) return [];
  const out: Array<{ position: number; major: boolean }> = [];
  let prevDate = '';
  for (let i = 0; i < timestamps.length; i++) {
    const iso = timestamps[i];
    const date = iso ? iso.slice(0, 10) : '';
    const major = date !== prevDate;
    prevDate = date;
    out.push({ position: i / (timestamps.length - 1), major });
  }
  return out;
}

function formatDateLabel(iso: string | undefined): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    }).format(d);
  } catch {
    return iso;
  }
}

